// src/components/NewVersionBanner.jsx
// Slim top banner that appears when a newer build has been uploaded to the
// server. Checks every 5 minutes and whenever the tab regains focus; never
// reloads on its own — the user clicks Refresh. Fails silent on any error.
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import {
  mainJsFromManifest,
  runningMainJs,
  isNewVersion,
} from "../utils/versionCheck";

const CHECK_EVERY_MS = 5 * 60 * 1000;

export default function NewVersionBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let stopped = false;
    const check = async () => {
      try {
        const res = await fetch("/asset-manifest.json", { cache: "no-store" });
        if (!res.ok) return;
        const manifest = await res.json();
        if (stopped) return;
        if (isNewVersion(runningMainJs(document), mainJsFromManifest(manifest))) {
          setShow(true);
        }
      } catch {
        /* dev server or offline — no manifest, no banner */
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    const timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!show) return null;
  return (
    <div className="fixed top-0 inset-x-0 z-[100] bg-indigo-600 text-white text-sm flex items-center justify-center gap-3 py-2 px-4 print:hidden">
      <span>New Version Available</span>
      <button
        onClick={() => window.location.reload()}
        className="inline-flex items-center gap-1.5 bg-white text-indigo-700 font-semibold rounded-md px-3 py-1 text-xs"
      >
        <RefreshCw size={12} /> Refresh
      </button>
    </div>
  );
}
